import { useEffect, useState } from 'react'
import { Text } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'

import {
  MappableAttendee,
  ParticipantMappingModal,
} from '@/components/Event/CrForm/components/ParticipantMappingModal'
import {
  applyMappingConfirmation,
  findUnmappedParticipant,
  hasParticipantLeft,
  isMappingArmed,
} from '@/components/Event/CrForm/participantsMapping'
import { canAddWitnessFeedback } from '@/components/Event/CrForm/witnesses'
import { useEventZoomParticipants } from '@/components/Event/NewScript/queries/scriptData'
import { AttendeeRole } from '@/constants'
import { Event, ReporterFeedback, ZoomParticipant } from '@/types'
import {
  getAttendeeRolesForEvent,
  isWitnessLocationRequired,
} from '@/utils/parrot'
import { formatTimeInTimezone, getEventTimezone } from '@/utils/time'

const MODAL_ID = 'cr-participant-mapping'

// The SA counterpart of the DR script's useParticipantsMapping: once Attendance is done,
// an unmapped Zoom participant opens a modal asking who they are. The polled list is
// shared through the query cache, so this observer costs no extra request.
export const useCrParticipantsMapping = ({
  event,
  form,
  witnessIndex,
}: {
  event: Event
  form: UseFormReturnType<ReporterFeedback>
  witnessIndex: number
}) => {
  const [openParticipant, setOpenParticipant] =
    useState<ZoomParticipant | null>(null)
  // Disarmed while our own modal is up, so the effect cannot re-fire mid-mapping.
  const armed =
    isMappingArmed(form.values, {
      isOngoing: event.is_ongoing,
      witnessIndex,
    }) && !openParticipant

  const { data: participants } = useEventZoomParticipants(event, {
    enabled: armed,
  })

  const attendees = (
    form.values.events_feedbacks?.[witnessIndex]?.attendees ?? []
  ).filter((attendee): attendee is MappableAttendee => !!attendee.id)

  // Duplicate checks span every section, because Confirm writes to every section.
  const allEventAttendees = (form.values.events_feedbacks ?? []).flatMap(
    feedback => feedback.attendees ?? [],
  )

  // Witness is offered only while another section can be appended.
  const canAddWitness = canAddWitnessFeedback(form.values, witnessIndex)
  const roles = getAttendeeRolesForEvent(event).filter(
    role => role !== AttendeeRole.WITNESS || canAddWitness,
  )

  useEffect(() => {
    if (!armed || !participants?.length) {
      return
    }

    const participant = findUnmappedParticipant(attendees, participants)
    if (!participant) {
      return
    }

    openMappingModal(participant)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, participants, attendees])

  // The modal renders in ModalsProvider, outside this hook's tree, so an unmount would
  // otherwise strand it with no close button and a dead form behind Confirm.
  useEffect(() => () => modals.close(MODAL_ID), [])

  useEffect(() => {
    // Left again: nothing to map. Disabled observers still see cached polls.
    if (openParticipant && hasParticipantLeft(participants, openParticipant)) {
      modals.close(MODAL_ID)
    }
  }, [participants, openParticipant])

  const openMappingModal = (participant: ZoomParticipant) => {
    modals.open({
      modalId: MODAL_ID,
      // Setting state synchronously here would block the modal from closing.
      onClose: () => setTimeout(() => setOpenParticipant(null)),
      title: (
        <Text size="lg" fw={700}>
          New Zoom user &quot;{participant.user_name}&quot; has just joined at{' '}
          {formatTimeInTimezone(participant.join_time, getEventTimezone(event))}
          .
        </Text>
      ),
      size: 740,
      withCloseButton: false,
      centered: true,
      closeOnClickOutside: false,
      children: (
        <ParticipantMappingModal
          participant={participant}
          attendees={attendees}
          allEventAttendees={allEventAttendees}
          roles={roles}
          witnessLocationEnabled={isWitnessLocationRequired(event)}
          onConfirm={({ attendeeIds, newAttendees }) => {
            // Updater form: this closure is captured at open time, so form.values here
            // would be stale.
            form.setValues(current =>
              applyMappingConfirmation({
                values: current as ReporterFeedback,
                event,
                participant,
                activeWitnessIndex: witnessIndex,
                attendeeIds,
                newAttendees,
              }),
            )
            form.clearErrors()

            // One per witness — a new section is easy to miss once the modal closes.
            newAttendees
              .filter(attendee => attendee.role === AttendeeRole.WITNESS)
              .forEach(attendee => {
                showNotification({
                  message: `New witness ${attendee.name} was added to this event.`,
                })
              })

            modals.close(MODAL_ID)
          }}
        />
      ),
    })
    setOpenParticipant(participant)
  }
}
